import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../components/Button";
import Container from "../components/Container";
import CurrentTariff from "../components/CurrentTariff";
import Header from "../components/Header";
import RippleLoader from "../components/RippleLoader";
import { useTariff } from "../context/TariffContext";
import { useUserInfo } from "../context/UserInfoContext";
import useRedirectIfNotInLocalStorage from "../hooks/useRedirectIfNotInLocalStorage";
import EN from "../locales/en";

const EnergyUsage = () => {
  useRedirectIfNotInLocalStorage(
    ["postcode", "address", "addressId"],
    "/address"
  );

  const navigate = useNavigate();
  const { mpan, mprn } = useParams();
  const { address } = useUserInfo();
  const {
    getCurrentTariff,
    estimatedMonthlySpend,
    getEstimatedMonthlySpend,
    userDefinedTariffInfo,
    currentTariff,
    isFetchingCurrentTariff,
    setIsFetchingCurrentTariff,
    isFetchingMonthlySpend,
    setIsFetchingMonthlySpend,
  } = useTariff();

  const changeAddress = () => {
    navigate("/address");
  };

  const backToHome = () => {
    navigate("/");
  };

  const [showEditTariffModal, setShowEditTariffModal] = useState(false);
  const [showEditTariffModalError, setShowEditTariffModalError] =
    useState(false);
  const [outageError, setOutageError] = useState(false);
  const [nonDomesticMeterError, setNonDomesticMeterError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (mpan) {
        setIsFetchingCurrentTariff(true);
        try {
          await getCurrentTariff(mpan, mprn);
        } catch (error) {
          if (
            error?.statusCode === 400 &&
            error?.body === "NON_DOMESTIC_METER"
          ) {
            setNonDomesticMeterError(true);
          } else {
            setOutageError(true);
            setShowEditTariffModal(true);
            setShowEditTariffModalError(true);
          }
        }
        setIsFetchingCurrentTariff(false);
      }
    };

    fetchData();
  }, [mpan]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchMonthlySpend = async () => {
      if (!currentTariff && !userDefinedTariffInfo) {
        return;
      }
      setIsFetchingMonthlySpend(true);
      await getEstimatedMonthlySpend();
      setIsFetchingMonthlySpend(false);
    };

    fetchMonthlySpend();
  }, [currentTariff, userDefinedTariffInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  let quotesLinks = `/quotes/${mpan}`;

  if (mprn) {
    quotesLinks = quotesLinks + `/${mprn}`;
  }

  return (
    <Container>
      {outageError || nonDomesticMeterError ? (
        <>
          <Header title="Something went wrong" />

          <p className="text-center my-6">
            {outageError
              ? "There's been an error fetching your energy usage. Please try again later."
              : "The meter is registered as a business meter and isn't compatible with this service. This service is for domestic meters only."}
          </p>

          {outageError && (
            <Button onClick={changeAddress} fullWidth>
              Try again
            </Button>
          )}

          {nonDomesticMeterError && (
            <Button onClick={backToHome} fullWidth>
              Back to home
            </Button>
          )}
        </>
      ) : (
        <>
          <Header title={EN.energy_usage.title} />

          <div className="border-y border-[#afb6b9] mt-6 py-6">
            <h3 className="text-md font-bold leading-[1.5] text-center">
              {EN.energy_usage.address_title}
            </h3>
            <p className="mt-1 text-center">{address}</p>
            <div className="mt-2 flex justify-center">
              <button
                onClick={changeAddress}
                className="text-primary underline inline-block text-center"
              >
                {EN.energy_usage.change_address_text}
              </button>
            </div>
          </div>

          <div className="mt-4 py-4 space-y-8">
            <div>
              <h2 className="text-[30px] font-bold leading-[1.2] text-primary text-center">
                {EN.energy_usage.usage_supplier_headline}
              </h2>
              <p className="mt-3 text-center">
                {EN.energy_usage.usage_supplier_text}
              </p>
            </div>

            <div>
              <h3 className="text-md font-bold text-center">
                {EN.energy_usage.estimated_monthly_spend_headline}
              </h3>
              {!(isFetchingCurrentTariff || isFetchingMonthlySpend) ? (
                <p className="mt-1 text-[22px] font-bold text-center">
                  £{estimatedMonthlySpend}
                </p>
              ) : (
                <div className="mt-2">
                  <RippleLoader />
                </div>
              )}
            </div>

            <Button
              fullWidth
              to={quotesLinks}
              disabled={isFetchingCurrentTariff || isFetchingMonthlySpend}
            >
              {isFetchingCurrentTariff || isFetchingMonthlySpend
                ? EN.energy_usage.compare_energy_prices_button_loading_text
                : EN.energy_usage.compare_energy_prices_button_text}
            </Button>

            <CurrentTariff
              showEditTariffModal={showEditTariffModal}
              setShowEditTariffModal={setShowEditTariffModal}
              showEditTariffModalError={showEditTariffModalError}
              setShowEditTariffModalError={setShowEditTariffModalError}
            />

            <div>
              <h2 className="text-[30px] font-bold leading-[1.2] text-primary text-center">
                {EN.energy_usage.switch_title}
              </h2>
              <p className="mt-3 text-center">{EN.energy_usage.switch_text}</p>
            </div>

            <Button
              fullWidth
              to={quotesLinks}
              disabled={isFetchingCurrentTariff || isFetchingMonthlySpend}
            >
              {isFetchingCurrentTariff || isFetchingMonthlySpend
                ? EN.energy_usage.compare_energy_prices_button_loading_text
                : EN.energy_usage.compare_energy_prices_button_text}
            </Button>

            <div className="text-center">
              <a href="/faqs" className="text-primary underline">
                {EN.energy_usage.faqs_link_text}
              </a>
            </div>
          </div>
        </>
      )}
    </Container>
  );
};

export default EnergyUsage;
